import { resolveScannedQr } from "@/lib/resolveScannedQr";
import { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MapPin, Camera, CameraOff, CheckCircle2, ShieldAlert, Crosshair, Users, CloudOff, RefreshCw } from "lucide-react";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { QueuedScanStatus } from "./QueuedScanStatus";

interface Group {
  id: string;
  name: string;
  semester: string;
  class: string;
  site_id: string | null;
  site_name_override: string | null;
  lat_override: number | null;
  lng_override: number | null;
  radius_override_m: number | null;
  approval_status: string;
  scan_start_time: string | null;
  scan_end_time: string | null;
  max_radius_m: number | null;
  field_sites: { name: string; latitude: number; longitude: number; radius_m: number } | null;
}

interface Member {
  student_id: string;
  students: { id: string; first_name: string; last_name: string; student_number: string; barcode_academic: string | null } | null;
}

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function LeaderScanPanel() {
  const { user } = useAuth();
  const { addToQueue, queue, isOnline, isSyncing } = useOfflineQueue();
  const [studentId, setStudentId] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [todayActive, setTodayActive] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [scannedToday, setScannedToday] = useState<Set<string>>(new Set());
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const today = format(new Date(), "yyyy-MM-dd");

  // Student IDs with a queued (offline) scan for THIS group today
  const queuedForGroup = useMemo(() => {
    if (!selectedGroup) return new Set<string>();
    const ids = new Set<string>();
    for (const item of queue) {
      if (item.type !== "insert") continue;
      const p = item.payload as any;
      if (p?.table === "attendance_records"
          && p?.data?.field_group_id === selectedGroup.id
          && p?.data?.attendance_date === today) {
        ids.add(p.data.student_id);
      }
    }
    return ids;
  }, [queue, selectedGroup, today]);

  // Resolve current user's student id
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("students").select("id").eq("user_id", user.id).maybeSingle();
      setStudentId((data as any)?.id ?? null);
    })();
  }, [user]);

  // Load groups where current user is leader
  useEffect(() => {
    if (!studentId) return;
    (async () => {
      const { data } = await supabase
        .from("field_groups")
        .select("*, field_sites(name, latitude, longitude, radius_m)")
        .eq("leader_student_id", studentId)
        .order("name");
      setGroups((data ?? []) as any);
      if ((data ?? []).length === 1) setSelectedGroup((data as any)[0]);
    })();
  }, [studentId]);

  // Watch geolocation continuously
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Load members + today's scans + check whether today is a placement day
  useEffect(() => {
    if (!selectedGroup) { setMembers([]); setScannedToday(new Set()); setTodayActive(false); return; }
    (async () => {
      const [{ data: m }, { data: d }, { data: ar }] = await Promise.all([
        supabase
          .from("field_group_members")
          .select("student_id, students(id, first_name, last_name, student_number, barcode_academic)")
          .eq("group_id", selectedGroup.id),
        supabase
          .from("field_group_dates")
          .select("id")
          .eq("group_id", selectedGroup.id)
          .eq("placement_date", today)
          .maybeSingle(),
        supabase
          .from("attendance_records")
          .select("student_id")
          .eq("field_group_id", selectedGroup.id)
          .eq("attendance_date", today)
          .eq("attendance_type", "tuition"),
      ]);
      setMembers((m ?? []) as any);
      setTodayActive(!!d);
      setScannedToday(new Set(((ar ?? []) as any[]).map((r) => r.student_id)));
    })();
  }, [selectedGroup?.id, today]);

  // Resolve effective site coords + radius (capped by max_radius_m)
  const site = selectedGroup
    ? (() => {
        const baseRadius = selectedGroup.radius_override_m ?? selectedGroup.field_sites?.radius_m ?? 150;
        const cap = selectedGroup.max_radius_m;
        return {
          name: selectedGroup.field_sites?.name ?? selectedGroup.site_name_override ?? "Site",
          lat: selectedGroup.lat_override ?? selectedGroup.field_sites?.latitude ?? null,
          lng: selectedGroup.lng_override ?? selectedGroup.field_sites?.longitude ?? null,
          radius: cap != null && cap < baseRadius ? cap : baseRadius,
        };
      })()
    : null;

  const distance = site && coords && site.lat != null && site.lng != null
    ? Math.round(haversine(coords, { lat: Number(site.lat), lng: Number(site.lng) }))
    : null;
  const insideFence = distance != null && site != null && distance <= site.radius;

  // Time window check (browser local time — server enforces Africa/Dar_es_Salaam too)
  const nowHM = format(new Date(), "HH:mm");
  const windowStart = selectedGroup?.scan_start_time?.slice(0, 5) ?? null;
  const windowEnd = selectedGroup?.scan_end_time?.slice(0, 5) ?? null;
  const withinWindow = !windowStart || !windowEnd || (nowHM >= windowStart && nowHM <= windowEnd);
  const isApproved = selectedGroup?.approval_status === "approved";
  const canScan = !!todayActive && isApproved && withinWindow && insideFence;

  async function startScanner() {
    if (!selectedGroup) return;
    setScanning(true);
    try {
      const html = new Html5Qrcode("rotation-reader");
      scannerRef.current = html;
      await html.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        (decoded) => handleScan(decoded),
        () => {},
      );
    } catch (e: any) {
      setScanning(false);
      toast.error("Camera error: " + (e?.message ?? e));
    }
  }

  async function stopScanner() {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch {}
    scannerRef.current = null;
    setScanning(false);
  }

  useEffect(() => () => { stopScanner(); }, []);

  const lastScanRef = useRef<{ code: string; ts: number } | null>(null);

  async function handleScan(decoded: string) {
    if (!selectedGroup || !coords || !canScan) return;
    const code = decoded.trim();
    if (!code) return;
    // Debounce identical scans within 3 seconds
    if (lastScanRef.current && lastScanRef.current.code === code && Date.now() - lastScanRef.current.ts < 3000) return;
    lastScanRef.current = { code, ts: Date.now() };

    const resolved = await resolveScannedQr(code, "academic", "rotation_leader_scanner");
    if (!resolved.ok) { toast.error(resolved.message ?? "QR rejected"); return; }
    const lookupCode = resolved.resolvedCode ?? code;

    // Find member by academic barcode
    const member = members.find((m) => m.students?.barcode_academic === lookupCode);
    if (!member?.students) {
      toast.error("Code not in this group's roster");
      return;
    }
    if (scannedToday.has(member.student_id) || queuedForGroup.has(member.student_id)) {
      toast.info(`${member.students.first_name} already scanned today`);
      return;
    }

    // Capture original scan time so the server keeps the real moment, even when synced later
    const scannedAtIso = new Date().toISOString();
    const record = {
      student_id: member.student_id,
      attendance_type: "tuition",
      attendance_date: today,
      field_group_id: selectedGroup.id,
      geo_lat: coords.lat,
      geo_lng: coords.lng,
      status: "present",
      scanned_by: user?.id,
      scanned_at: scannedAtIso,
    };

    // Offline → enqueue for later sync. Server still re-checks geofence on insert.
    if (!isOnline) {
      try {
        await addToQueue({
          type: "insert",
          label: `Rotation scan · ${member.students.first_name} ${member.students.last_name}`,
          payload: { table: "attendance_records", data: record },
        });
        toast.success(`📥 Queued ${member.students.first_name} (offline)`);
      } catch (e: any) {
        toast.error("Could not queue scan: " + (e?.message ?? e));
      }
      return;
    }

    const { error } = await supabase.from("attendance_records").insert(record);
    if (error) {
      // Network blip / RLS rejection → fall back to queue so the leader can keep scanning
      try {
        await addToQueue({
          type: "insert",
          label: `Rotation scan · ${member.students.first_name} ${member.students.last_name}`,
          payload: { table: "attendance_records", data: record },
        });
        toast.warning(`Saved offline — will retry: ${error.message}`);
      } catch {
        toast.error(error.message);
      }
      return;
    }
    setScannedToday((prev) => new Set(prev).add(member.student_id));
    toast.success(`✓ ${member.students.first_name} ${member.students.last_name}`);
  }

  if (!studentId) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Student account required</AlertTitle>
        <AlertDescription>Only students designated as a Field Group Leader can use this view.</AlertDescription>
      </Alert>
    );
  }
  if (groups.length === 0) {
    return (
      <Alert>
        <Users className="h-4 w-4" />
        <AlertTitle>You're not a Group Leader</AlertTitle>
        <AlertDescription>Ask your Head of Academic to assign you as the leader of a field group.</AlertDescription>
      </Alert>
    );
  }

  const queuedCount = queuedForGroup.size;
  const presentCount = scannedToday.size;
  const total = members.length;
  const accountedFor = presentCount + queuedCount;

  return (
    <div className="space-y-4">
      {groups.length > 1 && (
        <Card>
          <CardContent className="pt-6 space-y-2">
            <p className="text-xs text-muted-foreground">Pick a group to scan for:</p>
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <Button key={g.id} variant={selectedGroup?.id === g.id ? "default" : "outline"} size="sm" onClick={() => setSelectedGroup(g)}>
                  {g.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedGroup && site && (
        <>
          <QueuedScanStatus fieldGroupId={selectedGroup.id} />
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="w-4 h-4" /> {site.name}
              </CardTitle>
              <CardDescription>
                {selectedGroup.name} • {selectedGroup.class} • Semester {selectedGroup.semester}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isApproved ? (
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Group not yet approved</AlertTitle>
                  <AlertDescription>Your supervisor must approve this group's scan rules before you can record attendance.</AlertDescription>
                </Alert>
              ) : !todayActive ? (
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>No placement today</AlertTitle>
                  <AlertDescription>Today ({format(new Date(), "PP")}) is not scheduled for this group.</AlertDescription>
                </Alert>
              ) : !withinWindow ? (
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>Outside scan window</AlertTitle>
                  <AlertDescription>
                    Scanning is only allowed between <strong>{windowStart}</strong> and <strong>{windowEnd}</strong>. It is currently {nowHM}.
                  </AlertDescription>
                </Alert>
              ) : !coords ? (
                <Alert>
                  <Crosshair className="h-4 w-4" />
                  <AlertTitle>Waiting for location…</AlertTitle>
                  <AlertDescription>Please allow location access in your browser.</AlertDescription>
                </Alert>
              ) : !insideFence ? (
                <Alert variant="destructive">
                  <ShieldAlert className="h-4 w-4" />
                  <AlertTitle>You're outside the geofence</AlertTitle>
                  <AlertDescription>
                    You must be at <strong>{site.name}</strong> (within {site.radius}m) to scan.
                    {distance != null && ` You are currently ${distance}m away.`}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Ready to scan</AlertTitle>
                  <AlertDescription>
                    {distance}m from site centre.
                    {windowStart && windowEnd && ` Window ${windowStart}–${windowEnd}.`}
                  </AlertDescription>
                </Alert>
              )}

              {(!isOnline || queuedCount > 0) && (
                <Alert>
                  {isOnline
                    ? <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
                    : <CloudOff className="h-4 w-4" />}
                  <AlertTitle>
                    {isOnline
                      ? (isSyncing ? "Syncing queued scans…" : `${queuedCount} scan${queuedCount === 1 ? "" : "s"} pending sync`)
                      : "You are offline — scans will be queued"}
                  </AlertTitle>
                  <AlertDescription>
                    Queued scans keep their original timestamp and are sent automatically once you reconnect. The server still verifies your location for every scan.
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border rounded p-2"><p className="text-2xl font-bold">{total}</p><p className="text-[11px] text-muted-foreground">Roster</p></div>
                <div className="border rounded p-2">
                  <p className="text-2xl font-bold text-primary">{presentCount}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Scanned{queuedCount > 0 ? <> · <span className="text-muted-foreground font-medium">+{queuedCount} queued</span></> : null}
                  </p>
                </div>
                <div className="border rounded p-2"><p className="text-2xl font-bold">{Math.max(total - accountedFor, 0)}</p><p className="text-[11px] text-muted-foreground">Pending</p></div>
              </div>

              <div className="flex justify-center">
                {!scanning ? (
                  <Button onClick={startScanner} disabled={!canScan}>
                    <Camera className="w-4 h-4 mr-1" /> Start scanner
                  </Button>
                ) : (
                  <Button variant="outline" onClick={stopScanner}>
                    <CameraOff className="w-4 h-4 mr-1" /> Stop scanner
                  </Button>
                )}
              </div>
              <div id="rotation-reader" className="w-full max-w-sm mx-auto" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Roster</CardTitle>
              <CardDescription>Tap green tick once a student is scanned.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Reg #</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m, i) => (
                    <TableRow key={m.student_id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{m.students ? `${m.students.first_name} ${m.students.last_name}` : "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{m.students?.student_number ?? "—"}</TableCell>
                      <TableCell className="text-right">
                        {scannedToday.has(m.student_id) ? (
                          <Badge><CheckCircle2 className="h-3 w-3 mr-1" /> Present</Badge>
                        ) : queuedForGroup.has(m.student_id) ? (
                          <Badge variant="outline"><CloudOff className="h-3 w-3 mr-1" /> Queued</Badge>
                        ) : (
                          <Badge variant="secondary">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
